/* eslint-disable no-unused-vars */
import { useState, useCallback, useRef, useId, useMemo, useEffect } from 'react';
import { CreativeNameToggleDelete, CtaTitle, Message } from '../index';
import { useDispatch, useSelector } from 'react-redux';
import { creativesActions, formErrorsCampaign_action } from 'store/actions';
import styled, { css } from 'styled-components';
import {
  isAdminSelector,
  select_campaign_env,
  getCreativeMessage,
  select_current_campaign_obj,
} from 'store/selectors';
import { campaignEnvironment } from 'constants';
import { Separator, Tab } from 'components';

function isAllTokensUnique(list, currentToken, id) {
  if (list.length < 2) return true;
  return (
    list.length === new Set(list.map((item) => item.creative_id_token)).size &&
    list.every((item) => id === item.bn_bannerid || item.creative_id_token !== currentToken)
  );
}

const CTA_TITLE_MAX_SIZE = 25;

export const TopContainer = ({ isNewCreative, tab, setTab }) => {
  const dispatch = useDispatch();

  const isAdmin = useSelector(isAdminSelector);
  const creativesHavingError = useSelector((state) => state?.formErrorsCampaign.creatives);
  const environment = useSelector(select_campaign_env);
  const currentCampaignObj = useSelector(select_current_campaign_obj);
  const creativesList = useSelector((state) => state?.creatives?.creativesList);
  const { name, title, creative_id_token, bn_bannerid } = useSelector(
    (state) => state?.creatives?.selectedCreative,
  );
  const { fieldName, messageValue } = useSelector(getCreativeMessage);

  const isRuCampaigns =
    currentCampaignObj?.selectedLocationsList.includes('RU') ||
    currentCampaignObj?.selectedLocationsList === '|' ||
    currentCampaignObj?.selectedLocationsList === '';
  const prevIsRuCampaignsRef = useRef(isRuCampaigns);

  const isTokenRequired = !isAdmin && isRuCampaigns;
  const [isTokenEditable, setIsTokenEditable] = useState(isAdmin); // always editable for admin
  const initialTokens = useRef(
    creativesList?.map((item) => ({
      id: item.bn_bannerid,
      creative_id_token: item.creative_id_token,
    })),
  );

  useEffect(() => {
    // change only when user manually resets from 'not ru' to 'ru'
    if (!prevIsRuCampaignsRef.current && isRuCampaigns) {
      setIsTokenEditable(true);

      // if there were tokens form server - set them back and disable to edit
      if (initialTokens.current?.some((item) => item.creative_id_token !== '')) {
        dispatch(creativesActions.restoreCreativeTokenIDs(initialTokens.current));
        setIsTokenEditable(false);
      }
    }
    prevIsRuCampaignsRef.current = isRuCampaigns;
  }, [isRuCampaigns, isTokenEditable]);

  const validateFields = useCallback(
    (nextValues = {}) => {
      const nextMessageValue = nextValues[fieldName] ?? messageValue;
      const nextName = nextValues.name ?? name;
      const nextTitle = nextValues.title ?? title;
      const nextToken = nextValues.creative_id_token ?? creative_id_token;
      const objToCheck = {
        [fieldName]: nextMessageValue,
        name: nextName,
        title: nextTitle,
        creative_id_token: nextToken,
      };
      const allFieldsValid = Object.entries(objToCheck).every(([key, value]) =>
        key === 'creative_id_token'
          ? true
          : key === 'title'
          ? value && value.length > 2 && value.length <= CTA_TITLE_MAX_SIZE
          : value && value.length > 1,
      );

      const isValidToken = isTokenRequired
        ? !!nextToken && isAllTokensUnique(creativesList, nextToken, bn_bannerid)
        : true;
      const hasCreativesError = !(allFieldsValid && isValidToken);

      creativesHavingError !== hasCreativesError &&
        dispatch(
          formErrorsCampaign_action.updateErrorField({
            errorField: 'creatives',
            newValue: hasCreativesError,
          }),
        );
    },
    [
      isTokenRequired,
      fieldName,
      messageValue,
      name,
      title,
      creative_id_token,
      creativesList,
      creativesHavingError,
    ],
  );

  const onChangeParam = useCallback(
    (e, field, max_size) => {
      if (field !== 'creative_id_token' && max_size && e.target.value.length > max_size) return;

      const newValue =
        field === 'creative_id_token' ? e.target.value.replaceAll(' ', '') : e.target.value;

      validateFields({ [field]: newValue });

      dispatch(
        creativesActions.updateProperty({
          propField: field,
          newValue: newValue,
        }),
      );
    },
    [validateFields],
  );

  const tabIsDisabled = useMemo(() => {
    return (
      !messageValue ||
      !name ||
      !title ||
      messageValue?.length < 2 ||
      name?.length < 2 ||
      title?.length < 2 ||
      title?.length > CTA_TITLE_MAX_SIZE
    );
  }, [messageValue, name, title]);

  return (
    <>
      <CreativeNameToggleDelete
        onChangeParam={onChangeParam}
        isRuCampaigns={isRuCampaigns}
        isTokenEditable={isTokenEditable}
        isNewCreative={isNewCreative}
        validateFields={validateFields}
        initialTokens={initialTokens.current}
      />
      <div style={{ display: 'flex', gap: '24px', marginTop: '32px' }}>
        <Tab
          disabled={tabIsDisabled}
          pressed={tab === 'native'}
          value='native'
          label='Native Ads'
          onChange={setTab}
        />
        <Tab
          disabled={tabIsDisabled}
          pressed={tab === 'banners'}
          value='banners'
          label='Banners'
          onChange={setTab}
        />
      </div>
      <Separator />
      {tab === 'native' && (
        <DisabledBox
          isDisabled={!isAdmin && environment === campaignEnvironment.edit && !isNewCreative}
        >
          <CtaTitle onChangeParam={onChangeParam} />
          <Message onChangeParam={onChangeParam} />
        </DisabledBox>
      )}
    </>
  );
};

const DisabledBox = styled.div`
  ${({ isDisabled }) =>
    isDisabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`;
